import React, { useState } from 'react';

const ExportCenter = () => {
  const [exportLoading, setExportLoading] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [weeklyReportData, setWeeklyReportData] = useState(null);
  const [showWeeklyReport, setShowWeeklyReport] = useState(false);

  const handleExport = async (type, format) => {
    setExportLoading(true);
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/export/${type}/${format}`;
      
      // Add date filters if provided
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${type}_export.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        alert(`Erreur lors de l'export ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de l'export ${format.toUpperCase()}`);
    } finally {
      setExportLoading(false);
    }
  };

  const handleWeeklyReportExport = async (format) => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/export/weekly-report/${format}`;
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `rapport_hebdomadaire_${startDate || 'debut'}_${endDate || 'fin'}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        alert(`Erreur lors de l'export ${format.toUpperCase()}`);
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(`Erreur lors de l'export ${format.toUpperCase()}`);
    } finally {
      setExportLoading(false);
    }
  };

  const handlePreviewWeeklyReport = async () => {
    setExportLoading(true);
    try {
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/reports/weekly`;
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setWeeklyReportData(data);
        setShowWeeklyReport(true);
      } else {
        alert('Erreur lors du chargement du rapport');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors du chargement du rapport');
    } finally {
      setExportLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'XOF',
      minimumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  const handleComprehensiveReport = async () => {
    setExportLoading(true);
    try {
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/export/comprehensive-report/pdf`;
      
      // Add date filters if provided
      const params = new URLSearchParams();
      if (startDate) params.append('start_date', startDate);
      if (endDate) params.append('end_date', endDate);
      
      if (params.toString()) {
        url += `?${params.toString()}`;
      }

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = 'rapport_complet.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        alert('Erreur lors de la génération du rapport complet');
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert('Erreur lors de la génération du rapport complet');
    } finally {
      setExportLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-2">Centre d'Export</h1>
        <p className="text-gray-600">Exportez vos données vers différents formats</p>
      </div>

      {/* Date Range Filters */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h3 className="text-lg font-medium text-gray-800 mb-4">📅 Période d'Export</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de début
            </label>
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Date de fin
            </label>
            <input
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Nouveau Rapport Hebdomadaire */}
      <div className="bg-white rounded-lg shadow-md p-6 mb-6">
        <h2 className="text-lg font-semibold mb-4">📊 Rapport Hebdomadaire Opérationnel</h2>
        <p className="text-gray-600 mb-4">
          Rapport complet avec toutes les données opérationnelles : dates, quantités, transporteurs, tarifs, marges, statuts de paiement
        </p>
        
        <div className="flex flex-wrap gap-3 mb-4">
          <button
            onClick={handlePreviewWeeklyReport}
            disabled={exportLoading}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {exportLoading ? '⏳' : '👁️'} Aperçu
          </button>
          <button
            onClick={() => handleWeeklyReportExport('excel')}
            disabled={exportLoading}
            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
          >
            {exportLoading ? '⏳' : '📊'} Export Excel
          </button>
          <button
            onClick={() => handleWeeklyReportExport('csv')}
            disabled={exportLoading}
            className="px-4 py-2 bg-orange-600 text-white rounded-md hover:bg-orange-700 disabled:opacity-50 flex items-center gap-2"
          >
            {exportLoading ? '⏳' : '📋'} Export CSV
          </button>
        </div>

        {/* Aperçu du rapport */}
        {showWeeklyReport && weeklyReportData && (
          <div className="mt-6 border-t pt-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold">Aperçu du Rapport Hebdomadaire</h3>
              <p className="text-gray-600">
                Période : {weeklyReportData.period.start_date || 'Début'} - {weeklyReportData.period.end_date || 'Fin'}
              </p>
              <p className="text-gray-600">
                Total des enregistrements : {weeklyReportData.total_records}
              </p>
            </div>
            
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white border border-gray-300 rounded-lg">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Date</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Qté</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Transporteur</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Départ</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Arrivée</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Client</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Tarif Transport.</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Marge</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Tarif Client</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Total Client</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Commercial</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Statut</th>
                    <th className="px-2 py-2 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">Paiement</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {weeklyReportData.data.slice(0, 10).map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {formatDate(row.date_operation)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {row.quantite}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {row.transporteurs}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {row.depart}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {row.arrivee}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {row.client}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {formatCurrency(row.tarif_transporteur_fcfa)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {formatCurrency(row.marge_fcfa)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {formatCurrency(row.tarif_client_fcfa)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {formatCurrency(row.total_client)}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        {row.commercial_en_charge}
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          row.statut === 'terminée' ? 'bg-green-100 text-green-800' :
                          row.statut === 'en_cours' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {row.statut}
                        </span>
                      </td>
                      <td className="px-2 py-2 whitespace-nowrap text-sm text-gray-900 border-b">
                        <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          row.paiement_statut === 'Payé' ? 'bg-green-100 text-green-800' :
                          row.paiement_statut === 'Partiellement payé' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {row.paiement_statut}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              
              {weeklyReportData.data.length > 10 && (
                <div className="mt-4 text-center text-gray-600">
                  ... et {weeklyReportData.data.length - 10} autres enregistrements
                </div>
              )}
            </div>
            
            <div className="mt-4 text-right">
              <button
                onClick={() => setShowWeeklyReport(false)}
                className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700"
              >
                Fermer l'aperçu
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        
        {/* Documents Export */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-2xl">📄</span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">Documents</h3>
              <p className="text-sm text-gray-600">Export des documents</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => handleExport('documents', 'csv')}
              disabled={exportLoading}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportLoading ? '⏳' : '📊'} Export CSV
            </button>
            <button
              onClick={() => handleExport('documents', 'pdf')}
              disabled={exportLoading}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportLoading ? '⏳' : '📄'} Export PDF
            </button>
          </div>
        </div>

        {/* Operations Export */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-2xl">🚛</span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">Opérations</h3>
              <p className="text-sm text-gray-600">Export des opérations</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => handleExport('operations', 'csv')}
              disabled={exportLoading}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportLoading ? '⏳' : '📊'} Export CSV
            </button>
          </div>
        </div>

        {/* Opportunities Export */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-2xl">🎯</span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">Opportunités</h3>
              <p className="text-sm text-gray-600">Export des opportunités</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => handleExport('opportunities', 'csv')}
              disabled={exportLoading}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportLoading ? '⏳' : '📊'} Export CSV
            </button>
          </div>
        </div>

        {/* Activity Logs Export */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-2xl">📊</span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">Activités</h3>
              <p className="text-sm text-gray-600">Export des logs d'activité</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={() => handleExport('activity-logs', 'csv')}
              disabled={exportLoading}
              className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportLoading ? '⏳' : '📊'} Export CSV
            </button>
            <button
              onClick={() => handleExport('activity-logs', 'pdf')}
              disabled={exportLoading}
              className="w-full px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportLoading ? '⏳' : '📄'} Export PDF
            </button>
          </div>
        </div>

        {/* Comprehensive Report */}
        <div className="bg-white rounded-lg shadow-md p-6 md:col-span-2">
          <div className="flex items-center mb-4">
            <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
              <span className="text-2xl">📈</span>
            </div>
            <div>
              <h3 className="text-lg font-medium text-gray-800">Rapport Complet</h3>
              <p className="text-sm text-gray-600">Rapport complet avec tous les modules</p>
            </div>
          </div>
          <div className="space-y-3">
            <button
              onClick={handleComprehensiveReport}
              disabled={exportLoading}
              className="w-full px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {exportLoading ? '⏳' : '📊'} Générer Rapport Complet PDF
            </button>
            <p className="text-xs text-gray-500">
              Inclut un résumé exécutif et les données de tous les modules
            </p>
          </div>
        </div>
      </div>

      {/* Information */}
      <div className="mt-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <div className="flex-shrink-0">
            <span className="text-blue-400 text-xl">ℹ️</span>
          </div>
          <div className="ml-3">
            <h3 className="text-sm font-medium text-blue-800">Information</h3>
            <div className="mt-1 text-sm text-blue-700">
              <ul className="list-disc list-inside space-y-1">
                <li>Les exports CSV sont optimisés pour Excel et Google Sheets</li>
                <li>Les exports PDF sont formatés pour l'impression</li>
                <li>Les filtres de date sont optionnels - si vides, toutes les données sont exportées</li>
                <li>Le rapport complet inclut un résumé exécutif et les données de tous les modules</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExportCenter;